import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth, API_BASE } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import {
  BarChart3,
  Clock3,
  CheckCircle2,
  IndianRupee,
  RefreshCw,
  X,
  Loader2,
  UserPlus,
  ArrowRight,
  AlertCircle,
  Receipt,
  Phone,
  MapPin,
  FileDown,
  CalendarDays,
  Settings,
  ChevronDown,
  ChevronUp,
  Save,
  Sun,
} from 'lucide-react';

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const addDaysISO = (isoDate, days) => {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const mondayOfCurrentWeekISO = () => {
  const d = new Date();
  const day = d.getDay() || 7; // Sun=0 -> 7
  d.setDate(d.getDate() - day + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatRangePretty = (fromLocal, toLocal) => {
  const parse = (s) => {
    const [d, t] = s.split('T');
    const [y, mo, dd] = d.split('-').map(Number);
    const [hh, mm] = t.split(':').map(Number);
    return new Date(y, mo - 1, dd, hh, mm);
  };
  const opts = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true };
  return {
    from: parse(fromLocal).toLocaleString('en-IN', opts),
    to: parse(toLocal).toLocaleString('en-IN', opts),
  };
};

const formatINR = (n) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};

export default function ReportsPage() {
  const { user, getAuthHeader } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [summary, setSummary] = useState({ pending: { count: 0, amount: 0 }, paid: { count: 0, amount: 0 }, total: { count: 0, amount: 0 } });
  const [doctorRevenue, setDoctorRevenue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statementDoctor, setStatementDoctor] = useState(null); // { doctor_id }
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);

  // Daily/weekly/monthly/custom Excel — state
  const [reportMode, setReportMode] = useState('daily'); // daily | weekly | monthly | custom
  const [dailyDate, setDailyDate] = useState(todayISO());
  const [weekStart, setWeekStart] = useState(mondayOfCurrentWeekISO()); // Monday-based
  const [monthValue, setMonthValue] = useState(todayISO().slice(0, 7)); // YYYY-MM
  const [customFromDate, setCustomFromDate] = useState(todayISO());
  const [customFromTime, setCustomFromTime] = useState('09:00');
  const [customToDate, setCustomToDate] = useState(todayISO());
  const [customToTime, setCustomToTime] = useState('14:00');
  const [reportPreview, setReportPreview] = useState(null); // { from, to, label, count } after Generate
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportDownloading, setReportDownloading] = useState(false);
  const [reportMessage, setReportMessage] = useState('');

  const [settings, setSettings] = useState(null); // { shifts, role_access }
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');

  // Load report settings on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setSettingsLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/reports/settings`, { headers: getAuthHeader() });
        if (cancelled) return;
        setSettings(res.data);
        setSettingsDraft(JSON.parse(JSON.stringify(res.data)));
      } catch (err) {
        // swallow: user may not have access
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  // Compute currently-selected range (IST) → local ISO datetime strings for the API
  const currentRange = useMemo(() => {
    if (reportMode === 'daily') {
      const from = `${dailyDate}T00:00`;
      const to = `${addDaysISO(dailyDate, 1)}T00:00`;
      return { from, to, label: 'Daily' };
    }
    if (reportMode === 'weekly') {
      const from = `${weekStart}T00:00`;
      const to = `${addDaysISO(weekStart, 7)}T00:00`;
      return { from, to, label: 'Weekly' };
    }
    if (reportMode === 'monthly') {
      const [y, m] = monthValue.split('-').map(Number);
      const first = `${monthValue}-01`;
      const nextMonthDate = new Date(y, m, 1); // month index m = next month
      const yy = nextMonthDate.getFullYear();
      const mm = String(nextMonthDate.getMonth() + 1).padStart(2, '0');
      const dd = String(nextMonthDate.getDate()).padStart(2, '0');
      return { from: `${first}T00:00`, to: `${yy}-${mm}-${dd}T00:00`, label: 'Monthly' };
    }
    // custom
    return {
      from: `${customFromDate}T${customFromTime}`,
      to: `${customToDate}T${customToTime}`,
      label: 'Custom',
    };
  }, [reportMode, dailyDate, weekStart, monthValue, customFromDate, customFromTime, customToDate, customToTime]);

  const applyPresetChip = (preset) => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    setReportMode('custom');
    if (preset === 'today') {
      const today = iso(new Date(y, m, d));
      setCustomFromDate(today); setCustomFromTime('00:00');
      setCustomToDate(today); setCustomToTime('23:59');
    } else if (preset === 'today_morning') {
      const today = iso(new Date(y, m, d));
      setCustomFromDate(today); setCustomFromTime('09:00');
      setCustomToDate(today); setCustomToTime('13:00');
    } else if (preset === 'today_afternoon') {
      const today = iso(new Date(y, m, d));
      setCustomFromDate(today); setCustomFromTime('13:00');
      setCustomToDate(today); setCustomToTime('18:00');
    } else if (preset === 'yesterday') {
      const yest = iso(new Date(y, m, d - 1));
      setCustomFromDate(yest); setCustomFromTime('00:00');
      setCustomToDate(yest); setCustomToTime('23:59');
    } else if (preset === 'current_shift' || preset === 'previous_shift') {
      const shifts = settings?.shifts || [];
      const nowMins = now.getHours() * 60 + now.getMinutes();
      const shiftMins = (s) => {
        const [sh, sm] = s.start.split(':').map(Number);
        const [eh, em] = s.end.split(':').map(Number);
        return { s: sh * 60 + sm, e: eh * 60 + em };
      };
      const currentIdx = shifts.findIndex((s) => {
        const { s: st, e: en } = shiftMins(s);
        return nowMins >= st && nowMins < en;
      });
      let idx = preset === 'current_shift' ? currentIdx : currentIdx - 1;
      if (idx < 0) idx = preset === 'previous_shift' && shifts.length ? shifts.length - 1 : 0;
      const shift = shifts[idx];
      if (!shift) return;
      const today = iso(new Date(y, m, d));
      setCustomFromDate(today); setCustomFromTime(shift.start);
      setCustomToDate(today); setCustomToTime(shift.end);
    } else if (preset === 'this_week') {
      setReportMode('weekly');
      setWeekStart(mondayOfCurrentWeekISO());
    } else if (preset === 'this_month') {
      setReportMode('monthly');
      setMonthValue(todayISO().slice(0, 7));
    }
    setReportPreview(null);
    setReportMessage('');
  };

  const generateReport = async () => {
    setReportGenerating(true);
    setError('');
    setReportMessage('');
    setReportPreview(null);
    try {
      const url = reportMode === 'daily'
        ? `${API_BASE}/reports/daily-referrals-excel?date=${dailyDate}`
        : `${API_BASE}/reports/referrals-excel?from_dt=${encodeURIComponent(currentRange.from)}&to_dt=${encodeURIComponent(currentRange.to)}&label=${encodeURIComponent(currentRange.label)}`;
      const res = await axios.get(url, { headers: getAuthHeader(), responseType: 'blob' });
      const count = Number(res.headers['x-referral-count'] || 0);
      // Extract filename from content-disposition
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `RMS_Report.xlsx`;
      setReportPreview({
        from: currentRange.from,
        to: currentRange.to,
        label: currentRange.label,
        count,
        filename,
        blob: res.data,
      });
    } catch (err) {
      let msg = 'Failed to generate the report.';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch {}
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      setError(msg);
    } finally {
      setReportGenerating(false);
    }
  };

  const downloadReport = () => {
    if (!reportPreview?.blob) return;
    setReportDownloading(true);
    try {
      const url = URL.createObjectURL(new Blob([reportPreview.blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = reportPreview.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setReportMessage(`Downloaded ${reportPreview.filename} — ${reportPreview.count} referral(s).`);
    } finally {
      setReportDownloading(false);
    }
  };

  const saveSettings = async () => {
    if (!settingsDraft) return;
    setSettingsSaving(true);
    setSettingsMessage('');
    setError('');
    try {
      const payload = {
        shifts: settingsDraft.shifts,
        role_access: settingsDraft.role_access,
      };
      const res = await axios.patch(`${API_BASE}/reports/settings`, payload, { headers: getAuthHeader() });
      setSettings(res.data);
      setSettingsDraft(JSON.parse(JSON.stringify(res.data)));
      setSettingsMessage('Report settings saved.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const applyPreset = (preset) => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    const iso = (dt) => dt.toISOString().slice(0, 10);
    let from = '', to = '';
    if (preset === 'today') { from = to = iso(new Date(y, m, d)); }
    else if (preset === 'yesterday') { from = to = iso(new Date(y, m, d - 1)); }
    else if (preset === 'this_week') { const dow = now.getDay() || 7; from = iso(new Date(y, m, d - dow + 1)); to = todayISO(); }
    else if (preset === 'this_month') { from = iso(new Date(y, m, 1)); to = todayISO(); }
    else if (preset === 'prev_month') { from = iso(new Date(y, m - 1, 1)); to = iso(new Date(y, m, 0)); }
    setDateFrom(from); setDateTo(to);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [sumRes, revRes] = await Promise.all([
        axios.get(`${API_BASE}/reports/payment-summary`, { headers: getAuthHeader(), params }),
        axios.get(`${API_BASE}/reports/doctor-revenue`, { headers: getAuthHeader(), params }),
      ]);
      setSummary(sumRes.data);
      setDoctorRevenue(revRes.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load reports.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, getAuthHeader]);

  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
  }, [fetchAll]);

  const openStatement = async (doctorId) => {
    setStatementDoctor({ doctor_id: doctorId });
    setStatement(null);
    setStatementLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API_BASE}/reports/doctor-statement/${doctorId}`, { headers: getAuthHeader(), params });
      setStatement(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load doctor statement.');
      setStatementDoctor(null);
    } finally {
      setStatementLoading(false);
    }
  };

  const closeStatement = () => { setStatementDoctor(null); setStatement(null); };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <AppHeader />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400 flex items-center justify-center shrink-0">
            <BarChart3 className="w-6 h-6" />
          </div>
          <div>
            <h1 data-testid="reports-page-title" className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              {isAdmin ? 'Reports & Revenue' : 'Payment Reports'}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {isAdmin
                ? 'Referral revenue generated through doctors — pending, cleared, and per-doctor breakdown for the selected period.'
                : 'Pending and Paid payment reports. Download Excel for the selected date range.'}
            </p>
          </div>
        </div>

        {/* Excel Report Generator — Admin only */}
        {isAdmin && (
        <ReportGeneratorCard
          user={user}
          isAdmin={isAdmin}
          settings={settings}
          settingsDraft={settingsDraft}
          setSettingsDraft={setSettingsDraft}
          settingsOpen={settingsOpen}
          setSettingsOpen={setSettingsOpen}
          saveSettings={saveSettings}
          settingsSaving={settingsSaving}
          settingsMessage={settingsMessage}
          settingsLoading={settingsLoading}
          reportMode={reportMode}
          setReportMode={(m) => { setReportMode(m); setReportPreview(null); setReportMessage(''); }}
          dailyDate={dailyDate}
          setDailyDate={(v) => { setDailyDate(v); setReportPreview(null); setReportMessage(''); }}
          weekStart={weekStart}
          setWeekStart={(v) => { setWeekStart(v); setReportPreview(null); setReportMessage(''); }}
          monthValue={monthValue}
          setMonthValue={(v) => { setMonthValue(v); setReportPreview(null); setReportMessage(''); }}
          customFromDate={customFromDate}
          setCustomFromDate={(v) => { setCustomFromDate(v); setReportPreview(null); setReportMessage(''); }}
          customFromTime={customFromTime}
          setCustomFromTime={(v) => { setCustomFromTime(v); setReportPreview(null); setReportMessage(''); }}
          customToDate={customToDate}
          setCustomToDate={(v) => { setCustomToDate(v); setReportPreview(null); setReportMessage(''); }}
          customToTime={customToTime}
          setCustomToTime={(v) => { setCustomToTime(v); setReportPreview(null); setReportMessage(''); }}
          applyPresetChip={applyPresetChip}
          generateReport={generateReport}
          reportGenerating={reportGenerating}
          reportPreview={reportPreview}
          downloadReport={downloadReport}
          reportDownloading={reportDownloading}
          reportMessage={reportMessage}
          currentRange={currentRange}
        />
        )}

        {/* Payment Reports card — Admin + Accountant */}
        <PaymentReportsCard
          user={user}
          settings={settings}
          getAuthHeader={getAuthHeader}
        />

        {/* Admin-only sections below (date range + revenue summary + doctor revenue table) */}
        {isAdmin && (<>
        {/* Date presets + custom range */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'today', label: 'Today' },
              { key: 'yesterday', label: 'Yesterday' },
              { key: 'this_week', label: 'This Week' },
              { key: 'this_month', label: 'This Month' },
              { key: 'prev_month', label: 'Previous Month' },
              { key: 'all', label: 'All Time' },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p.key === 'all' ? '' : p.key)}
                data-testid={`report-preset-${p.key}`}
                className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 hover:border-teal-700 text-slate-300 hover:text-white transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input data-testid="report-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
            <input data-testid="report-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
            <button onClick={fetchAll} data-testid="report-refresh-btn" className="px-3 py-2 bg-teal-500 hover:bg-teal-400 text-slate-950 text-xs font-semibold rounded-xl inline-flex items-center justify-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {error && (
          <div data-testid="reports-error-alert" className="p-3 bg-red-950/60 border border-red-800/80 rounded-xl text-red-200 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{error}
          </div>
        )}

        {/* Revenue summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BigCard title="Total Referral Amount Generated" count={summary.total.count} amount={summary.total.amount} testId="report-total" color="teal" icon={<Receipt className="w-4 h-4" />} caption="= Paid + Pending" />
          <BigCard title="Paid" count={summary.paid.count} amount={summary.paid.amount} testId="report-paid" color="emerald" icon={<CheckCircle2 className="w-4 h-4" />} />
          <BigCard title="Pending" count={summary.pending.count} amount={summary.pending.amount} testId="report-pending" color="amber" icon={<Clock3 className="w-4 h-4" />} />
        </div>

        {/* Doctor Revenue Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-white flex items-center gap-2"><UserPlus className="w-4 h-4 text-teal-400" /> Referral Doctor Revenue</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">Revenue generated through each referral doctor in the selected period.</p>
            </div>
            <span className="text-[11px] text-slate-500" data-testid="doctor-rev-count">{doctorRevenue.length} doctor{doctorRevenue.length === 1 ? '' : 's'}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="doctor-revenue-table">
              <thead>
                <tr className="bg-slate-950/80 border-b border-slate-800 text-[10px] uppercase tracking-widest text-slate-400">
                  <th className="py-3 px-4 font-semibold">Referral Doctor</th>
                  <th className="py-3 px-4 font-semibold text-right">Total Referrals</th>
                  <th className="py-3 px-4 font-semibold text-right">Total Amount</th>
                  <th className="py-3 px-4 font-semibold text-right">Paid</th>
                  <th className="py-3 px-4 font-semibold text-right">Pending</th>
                  <th className="py-3 px-4 font-semibold text-right">Statement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {doctorRevenue.length === 0 ? (
                  <tr><td colSpan={6} className="py-10 text-center text-slate-500">{loading ? 'Loading…' : 'No doctor revenue in the selected period.'}</td></tr>
                ) : (
                  doctorRevenue.map((r) => (
                    <tr key={r.referral_doctor_id} data-testid={`doctor-revenue-row-${r.referral_doctor_id}`} className="hover:bg-slate-800/30">
                      <td className="py-3 px-4">
                        <div className="text-white font-medium">{r.referral_doctor_name}</div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                          {r.village && <><MapPin className="w-3 h-3" />{r.village}</>}
                          {r.phone_number && <><Phone className="w-3 h-3 ml-1" />{r.phone_number}</>}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right text-white tabular-nums">{r.total_referrals}</td>
                      <td className="py-3 px-4 text-right text-white font-semibold tabular-nums"><span className="inline-flex items-center gap-0.5"><IndianRupee className="w-3 h-3 opacity-60" />{formatINR(r.total_amount)}</span></td>
                      <td className="py-3 px-4 text-right text-emerald-300 tabular-nums"><span className="inline-flex items-center gap-0.5"><IndianRupee className="w-3 h-3 opacity-60" />{formatINR(r.paid_amount)}</span></td>
                      <td className="py-3 px-4 text-right text-amber-300 tabular-nums"><span className="inline-flex items-center gap-0.5"><IndianRupee className="w-3 h-3 opacity-60" />{formatINR(r.pending_amount)}</span></td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => openStatement(r.referral_doctor_id)} data-testid={`open-statement-btn-${r.referral_doctor_id}`} className="text-xs px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 rounded-lg inline-flex items-center gap-1.5">
                          Open <ArrowRight className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>)}
      </main>

      {statementDoctor && (
        <DoctorStatementModal
          data={statement}
          loading={statementLoading}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onClose={closeStatement}
        />
      )}
    </div>
  );
}

function ReportGeneratorCard(props) {
  const {
    user, isAdmin, settings, settingsDraft, setSettingsDraft, settingsOpen, setSettingsOpen,
    saveSettings, settingsSaving, settingsMessage, settingsLoading,
    reportMode, setReportMode,
    dailyDate, setDailyDate, weekStart, setWeekStart, monthValue, setMonthValue,
    customFromDate, setCustomFromDate, customFromTime, setCustomFromTime,
    customToDate, setCustomToDate, customToTime, setCustomToTime,
    applyPresetChip, generateReport, reportGenerating,
    reportPreview, downloadReport, reportDownloading, reportMessage,
    currentRange,
  } = props;

  const hasAccess = isAdmin;  // Admin only (final spec §1)

  const modes = [
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'custom', label: 'Custom' },
  ];

  const presetChips = [
    { key: 'today', label: 'Today' },
    { key: 'today_morning', label: 'Today 9 AM – 1 PM' },
    { key: 'today_afternoon', label: 'Today 1 PM – 6 PM' },
    { key: 'current_shift', label: 'Current Shift' },
    { key: 'previous_shift', label: 'Previous Shift' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'this_week', label: 'This Week' },
    { key: 'this_month', label: 'This Month' },
  ];

  const rangePretty = formatRangePretty(currentRange.from, currentRange.to);

  return (
    <div data-testid="report-generator-card" className="bg-gradient-to-br from-slate-900 via-slate-900 to-teal-950/30 border border-slate-800 rounded-2xl p-5 shadow-lg space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 flex items-center justify-center shrink-0">
            <FileDown className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white">Referral Excel Reports</h2>
            <p className="text-[12px] text-slate-400 mt-0.5 max-w-lg">
              Pick Daily / Weekly / Monthly or a Custom date &amp; time range. One row per referral, INR with 2 decimals, totals row at the bottom.
            </p>
          </div>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            data-testid="toggle-report-settings-btn"
            className="text-[11px] px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:border-teal-700 text-slate-300 hover:text-white inline-flex items-center gap-1.5"
          >
            <Settings className="w-3.5 h-3.5" />
            Shift & Access
            {settingsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      {!hasAccess && !settingsLoading && (
        <div data-testid="no-report-access" className="p-3 bg-amber-950/60 border border-amber-800/60 rounded-xl text-amber-200 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Your role currently does not have access to download referral reports. Ask an admin to enable your role in Shift & Access.
        </div>
      )}

      {/* Admin Shift & Access editor */}
      {isAdmin && settingsOpen && settingsDraft && (
        <div data-testid="report-settings-panel" className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sun className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-semibold text-white">Shift Timings</h3>
            </div>
            <div className="space-y-2">
              {(settingsDraft.shifts || []).map((s, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <input
                    data-testid={`shift-name-${idx}`}
                    type="text"
                    value={s.name}
                    onChange={(e) => {
                      const next = { ...settingsDraft, shifts: [...settingsDraft.shifts] };
                      next.shifts[idx] = { ...s, name: e.target.value };
                      setSettingsDraft(next);
                    }}
                    className="sm:col-span-6 px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-white focus:outline-none focus:border-teal-500"
                  />
                  <input
                    data-testid={`shift-start-${idx}`}
                    type="time"
                    value={s.start}
                    onChange={(e) => {
                      const next = { ...settingsDraft, shifts: [...settingsDraft.shifts] };
                      next.shifts[idx] = { ...s, start: e.target.value };
                      setSettingsDraft(next);
                    }}
                    className="sm:col-span-2 px-2 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-white focus:outline-none focus:border-teal-500"
                  />
                  <input
                    data-testid={`shift-end-${idx}`}
                    type="time"
                    value={s.end}
                    onChange={(e) => {
                      const next = { ...settingsDraft, shifts: [...settingsDraft.shifts] };
                      next.shifts[idx] = { ...s, end: e.target.value };
                      setSettingsDraft(next);
                    }}
                    className="sm:col-span-2 px-2 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-white focus:outline-none focus:border-teal-500"
                  />
                  <button
                    type="button"
                    data-testid={`remove-shift-${idx}`}
                    onClick={() => {
                      const next = { ...settingsDraft, shifts: settingsDraft.shifts.filter((_, i) => i !== idx) };
                      setSettingsDraft(next);
                    }}
                    className="sm:col-span-2 text-[11px] text-rose-300 hover:text-rose-200 hover:bg-rose-950/40 border border-rose-900/40 rounded-lg py-1.5"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="add-shift-btn"
                onClick={() => {
                  const next = { ...settingsDraft, shifts: [...(settingsDraft.shifts || []), { name: `Shift ${settingsDraft.shifts.length + 1}`, start: '08:00', end: '12:00' }] };
                  setSettingsDraft(next);
                }}
                className="text-[11px] text-teal-300 hover:text-teal-200 hover:bg-teal-950/40 border border-teal-900/40 rounded-lg px-3 py-1.5"
              >
                + Add Shift
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-4 h-4 text-teal-400" />
              <h3 className="text-sm font-semibold text-white">Report Access by Role</h3>
              <span className="text-[10px] text-slate-500">(admin & accountant always allowed; lab_staff & pharmacist always blocked)</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {['reception', 'staff'].map((role) => (
                <label key={role} className="flex items-center gap-2 px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-xs text-slate-300">
                  <input
                    type="checkbox"
                    data-testid={`role-access-${role}`}
                    checked={!!settingsDraft.role_access?.[role]}
                    onChange={(e) => {
                      const next = { ...settingsDraft, role_access: { ...settingsDraft.role_access, [role]: e.target.checked } };
                      setSettingsDraft(next);
                    }}
                  />
                  <span className="capitalize">{role.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            {settingsMessage && (
              <span data-testid="settings-saved-msg" className="text-[11px] text-emerald-300">{settingsMessage}</span>
            )}
            <button
              type="button"
              onClick={saveSettings}
              disabled={settingsSaving}
              data-testid="save-report-settings-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-slate-950 text-xs font-semibold rounded-lg"
            >
              {settingsSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* Mode Tabs */}
      <div className="inline-flex flex-wrap items-center gap-1 bg-slate-950 border border-slate-800 rounded-xl p-1">
        {modes.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setReportMode(m.key)}
            data-testid={`report-mode-${m.key}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
              reportMode === m.key ? 'bg-teal-500 text-slate-950 shadow-sm shadow-teal-500/20' : 'text-slate-400 hover:text-white'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        {presetChips.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => applyPresetChip(p.key)}
            data-testid={`report-preset-chip-${p.key}`}
            className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 hover:border-teal-700 text-slate-300 hover:text-white transition-colors"
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Mode-specific inputs */}
      <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4">
        {reportMode === 'daily' && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[11px] uppercase tracking-widest text-slate-400">Date</label>
            <input
              data-testid="report-daily-date"
              type="date"
              value={dailyDate}
              max={todayISO()}
              onChange={(e) => setDailyDate(e.target.value)}
              className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
            />
          </div>
        )}
        {reportMode === 'weekly' && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[11px] uppercase tracking-widest text-slate-400">Week Starting (Mon)</label>
            <input
              data-testid="report-week-start"
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
            />
          </div>
        )}
        {reportMode === 'monthly' && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[11px] uppercase tracking-widest text-slate-400">Month</label>
            <input
              data-testid="report-month"
              type="month"
              value={monthValue}
              onChange={(e) => setMonthValue(e.target.value)}
              className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
            />
          </div>
        )}
        {reportMode === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-400 block mb-1.5">From Date &amp; Time</label>
              <div className="flex gap-2">
                <input
                  data-testid="custom-from-date"
                  type="date"
                  value={customFromDate}
                  onChange={(e) => setCustomFromDate(e.target.value)}
                  className="flex-1 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                />
                <input
                  data-testid="custom-from-time"
                  type="time"
                  value={customFromTime}
                  onChange={(e) => setCustomFromTime(e.target.value)}
                  className="w-28 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-400 block mb-1.5">To Date &amp; Time</label>
              <div className="flex gap-2">
                <input
                  data-testid="custom-to-date"
                  type="date"
                  value={customToDate}
                  onChange={(e) => setCustomToDate(e.target.value)}
                  className="flex-1 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                />
                <input
                  data-testid="custom-to-time"
                  type="time"
                  value={customToTime}
                  onChange={(e) => setCustomToTime(e.target.value)}
                  className="w-28 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="text-[11px] text-slate-400">
            <span className="uppercase tracking-widest text-slate-500">Range</span>{' '}
            <span data-testid="range-preview-from" className="text-slate-200 font-medium">{rangePretty.from}</span>
            <span className="text-slate-500 mx-1">→</span>
            <span data-testid="range-preview-to" className="text-slate-200 font-medium">{rangePretty.to}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={generateReport}
              disabled={!hasAccess || reportGenerating}
              data-testid="generate-report-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 text-sm font-semibold rounded-xl shadow-md shadow-teal-500/20"
            >
              {reportGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {reportGenerating ? 'Generating…' : 'Generate Report'}
            </button>
            <button
              onClick={downloadReport}
              disabled={!reportPreview || reportDownloading}
              data-testid="download-report-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 text-sm font-semibold rounded-xl shadow-md shadow-emerald-500/20"
            >
              <FileDown className="w-4 h-4" />
              Download Excel
            </button>
          </div>
        </div>
      </div>

      {reportPreview && (
        <div data-testid="report-preview-card" className="bg-slate-950/60 border border-emerald-800/60 rounded-2xl p-3 text-xs text-emerald-200 flex items-center gap-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" />
          <div>
            Report generated: <span data-testid="report-preview-count" className="font-semibold">{reportPreview.count}</span>{' '}
            referral(s) between <span className="text-white">{formatRangePretty(reportPreview.from, reportPreview.to).from}</span>{' '}
            and <span className="text-white">{formatRangePretty(reportPreview.from, reportPreview.to).to}</span>.{' '}
            Click <span className="text-emerald-300 font-semibold">Download Excel</span> to save{' '}
            <span data-testid="report-preview-filename" className="font-mono text-emerald-300">{reportPreview.filename}</span>.
          </div>
        </div>
      )}

      {reportMessage && (
        <div data-testid="report-download-msg" className="text-[12px] text-emerald-300 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {reportMessage}
        </div>
      )}
    </div>
  );
}

function PaymentReportsCard({ user, settings, getAuthHeader }) {
  // Admin + Accountant only (final spec §2)
  const hasAccess = user?.role === 'admin' || user?.role === 'accountant';

  const [mode, setMode] = useState('daily');
  const [dailyDate, setDailyDate] = useState(todayISO());
  const [weekStart, setWeekStart] = useState(mondayOfCurrentWeekISO());
  const [monthValue, setMonthValue] = useState(todayISO().slice(0, 7));
  const [fromDate, setFromDate] = useState(todayISO());
  const [fromTime, setFromTime] = useState('00:00');
  const [toDate, setToDate] = useState(todayISO());
  const [toTime, setToTime] = useState('23:59');
  const [variant, setVariant] = useState('all'); // all|pending|cleared|doctor|accountant
  const [basis, setBasis] = useState('referral'); // referral|payment

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [downloading, setDownloading] = useState(false);

  const range = useMemo(() => {
    if (mode === 'daily') return { from: `${dailyDate}T00:00`, to: `${addDaysISO(dailyDate, 1)}T00:00`, label: 'Daily' };
    if (mode === 'weekly') return { from: `${weekStart}T00:00`, to: `${addDaysISO(weekStart, 7)}T00:00`, label: 'Weekly' };
    if (mode === 'monthly') {
      const [y, m] = monthValue.split('-').map(Number);
      const nxt = new Date(y, m, 1);
      const nxtStr = `${nxt.getFullYear()}-${String(nxt.getMonth() + 1).padStart(2, '0')}-${String(nxt.getDate()).padStart(2, '0')}`;
      return { from: `${monthValue}-01T00:00`, to: `${nxtStr}T00:00`, label: 'Monthly' };
    }
    return { from: `${fromDate}T${fromTime}`, to: `${toDate}T${toTime}`, label: 'Custom' };
  }, [mode, dailyDate, weekStart, monthValue, fromDate, fromTime, toDate, toTime]);

  const rangePretty = formatRangePretty(range.from, range.to);

  const applyPreset = (p) => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    if (p === 'today') { setMode('daily'); setDailyDate(iso(new Date(y, m, d))); }
    else if (p === 'yesterday') { setMode('daily'); setDailyDate(iso(new Date(y, m, d - 1))); }
    else if (p === 'this_week') { setMode('weekly'); setWeekStart(mondayOfCurrentWeekISO()); }
    else if (p === 'last_week') {
      const monday = new Date(); const day = monday.getDay() || 7;
      monday.setDate(monday.getDate() - day + 1 - 7);
      setMode('weekly'); setWeekStart(iso(monday));
    }
    else if (p === 'this_month') { setMode('monthly'); setMonthValue(todayISO().slice(0, 7)); }
    else if (p === 'last_month') {
      const dt = new Date(y, m - 1, 1);
      setMode('monthly');
      setMonthValue(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`);
    }
    setSummary(null); setMsg(''); setError('');
  };

  const loadSummary = useCallback(async () => {
    if (!hasAccess) return;
    setSummaryLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/reports/payment-summary-range`, {
        headers: getAuthHeader(),
        params: { from_dt: range.from, to_dt: range.to, basis },
      });
      setSummary(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load summary.');
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [hasAccess, getAuthHeader, range.from, range.to, basis]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const downloadExcel = async () => {
    if (!hasAccess) return;
    setDownloading(true); setError(''); setMsg('');
    try {
      const res = await axios.get(`${API_BASE}/reports/payments-excel`, {
        headers: getAuthHeader(),
        params: { from_dt: range.from, to_dt: range.to, variant, basis, label: range.label },
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `RMS_Payment_${range.label}.xlsx`;
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg(`Downloaded ${filename} — ${res.headers['x-referral-count']} referral(s).`);
    } catch (err) {
      let m = 'Failed to download payment report.';
      if (err.response?.data instanceof Blob) {
        try { const t = await err.response.data.text(); m = JSON.parse(t).detail || m; } catch {}
      } else if (err.response?.data?.detail) { m = err.response.data.detail; }
      setError(m);
    } finally {
      setDownloading(false);
    }
  };

  const variants = [
    { key: 'all', label: 'All Payments', desc: '10 cols — Pending + Paid' },
    { key: 'pending', label: 'Pending Only', desc: '6 cols — outstanding referrals' },
    { key: 'cleared', label: 'Cleared Only', desc: '8 cols — paid referrals with voucher' },
    { key: 'doctor', label: 'Doctor-wise', desc: 'Aggregated by referral doctor' },
    { key: 'accountant', label: 'Accountant Clearance', desc: '6 cols — paid summary for cashier' },
  ];

  return (
    <div data-testid="payment-reports-card" className="bg-gradient-to-br from-slate-900 via-slate-900 to-purple-950/30 border border-slate-800 rounded-2xl p-5 shadow-lg space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-300 flex items-center justify-center shrink-0">
          <Receipt className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-white">Payment Reports</h2>
          <p className="text-[12px] text-slate-400 mt-0.5 max-w-lg">
            Dedicated reports for Pending, Cleared, Doctor-wise and Accountant clearance. Switch the report basis between Referral Creation Date and Payment Clearance Date.
          </p>
        </div>
      </div>

      {!hasAccess && (
        <div data-testid="no-payment-access" className="p-3 bg-amber-950/60 border border-amber-800/60 rounded-xl text-amber-200 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Your role does not have access to payment reports. Admin can enable it under Shift & Access.
        </div>
      )}

      {/* Mode + Presets */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="inline-flex items-center gap-1 bg-slate-950 border border-slate-800 rounded-xl p-1">
          {['daily', 'weekly', 'monthly', 'custom'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => { setMode(m); setSummary(null); setMsg(''); }}
              data-testid={`payment-mode-${m}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg capitalize transition-all ${mode === m ? 'bg-purple-500 text-slate-950 shadow-sm' : 'text-slate-400 hover:text-white'}`}
            >
              {m}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-1 bg-slate-950 border border-slate-800 rounded-xl p-1">
          {[{ k: 'referral', l: 'By Referral Date' }, { k: 'payment', l: 'By Payment Date' }].map((b) => (
            <button
              key={b.k}
              type="button"
              onClick={() => { setBasis(b.k); setSummary(null); }}
              data-testid={`payment-basis-${b.k}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${basis === b.k ? 'bg-teal-500 text-slate-950 shadow-sm' : 'text-slate-400 hover:text-white'}`}
            >
              {b.l}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { k: 'today', l: 'Today' },
          { k: 'yesterday', l: 'Yesterday' },
          { k: 'this_week', l: 'This Week' },
          { k: 'last_week', l: 'Last Week' },
          { k: 'this_month', l: 'This Month' },
          { k: 'last_month', l: 'Last Month' },
        ].map((p) => (
          <button
            key={p.k}
            type="button"
            onClick={() => applyPreset(p.k)}
            data-testid={`payment-preset-${p.k}`}
            className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-800 hover:border-purple-700 text-slate-300 hover:text-white"
          >
            {p.l}
          </button>
        ))}
      </div>

      {/* Mode inputs */}
      <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 space-y-3">
        {mode === 'daily' && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[11px] uppercase tracking-widest text-slate-400">Date</label>
            <input data-testid="payment-daily-date" type="date" value={dailyDate} max={todayISO()} onChange={(e) => { setDailyDate(e.target.value); setSummary(null); }} className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500" />
          </div>
        )}
        {mode === 'weekly' && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[11px] uppercase tracking-widest text-slate-400">Week Starting (Mon)</label>
            <input data-testid="payment-week-start" type="date" value={weekStart} onChange={(e) => { setWeekStart(e.target.value); setSummary(null); }} className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500" />
          </div>
        )}
        {mode === 'monthly' && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[11px] uppercase tracking-widest text-slate-400">Month</label>
            <input data-testid="payment-month" type="month" value={monthValue} onChange={(e) => { setMonthValue(e.target.value); setSummary(null); }} className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500" />
          </div>
        )}
        {mode === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-400 block mb-1.5">From Date &amp; Time</label>
              <div className="flex gap-2">
                <input data-testid="payment-from-date" type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setSummary(null); }} className="flex-1 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500" />
                <input data-testid="payment-from-time" type="time" value={fromTime} onChange={(e) => { setFromTime(e.target.value); setSummary(null); }} className="w-28 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-400 block mb-1.5">To Date &amp; Time</label>
              <div className="flex gap-2">
                <input data-testid="payment-to-date" type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setSummary(null); }} className="flex-1 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500" />
                <input data-testid="payment-to-time" type="time" value={toTime} onChange={(e) => { setToTime(e.target.value); setSummary(null); }} className="w-28 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500" />
              </div>
            </div>
          </div>
        )}

        <div className="text-[11px] text-slate-400">
          <span className="uppercase tracking-widest text-slate-500">Range</span>{' '}
          <span data-testid="payment-range-from" className="text-slate-200">{rangePretty.from}</span>
          <span className="text-slate-500 mx-1">→</span>
          <span data-testid="payment-range-to" className="text-slate-200">{rangePretty.to}</span>
          <span className="ml-3 text-slate-500">Basis:</span>{' '}
          <span data-testid="payment-basis-label" className="text-slate-200">{basis === 'payment' ? 'Payment Clearance Date' : 'Referral Creation Date'}</span>
        </div>
      </div>

      {/* Summary chips */}
      {hasAccess && (
        <div data-testid="payment-summary-strip" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SummaryChip testId="payment-total-generated" label="Total Generated" amount={summary?.total_amount} count={summary?.total_count} color="text-white" loading={summaryLoading} icon={<IndianRupee className="w-4 h-4" />} />
          <SummaryChip testId="payment-total-paid" label="Total Paid" amount={summary?.paid_amount} count={summary?.paid_count} color="text-emerald-300" loading={summaryLoading} icon={<CheckCircle2 className="w-4 h-4" />} />
          <SummaryChip testId="payment-total-pending" label="Total Pending" amount={summary?.pending_amount} count={summary?.pending_count} color="text-amber-300" loading={summaryLoading} icon={<Clock3 className="w-4 h-4" />} />
        </div>
      )}

      {/* Variant grid */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">Choose a Report Variant</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {variants.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setVariant(v.key)}
              data-testid={`payment-variant-${v.key}`}
              className={`text-left px-3 py-2 rounded-xl border transition-all ${
                variant === v.key ? 'bg-purple-500/15 border-purple-500/60 text-white' : 'bg-slate-950/40 border-slate-800 text-slate-300 hover:border-purple-700'
              }`}
            >
              <div className="text-sm font-semibold flex items-center gap-1.5">{v.label}</div>
              <div className="text-[11px] text-slate-400">{v.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div data-testid="payment-report-error" className="p-2.5 bg-red-950/60 border border-red-800/70 rounded-xl text-red-200 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          onClick={downloadExcel}
          disabled={!hasAccess || downloading}
          data-testid="download-payment-excel-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 text-sm font-semibold rounded-xl shadow-md shadow-emerald-500/20"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          {downloading ? 'Preparing…' : 'Download Excel'}
        </button>
      </div>

      {msg && (
        <div data-testid="payment-download-msg" className="text-[12px] text-emerald-300 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {msg}
        </div>
      )}
    </div>
  );
}

function SummaryChip({ testId, label, amount, count, color, loading, icon }) {
  return (
    <div data-testid={testId} className="bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 mb-1">
        {icon}
        {label}
      </div>
      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : (
        <>
          <div className={`text-xl font-bold tabular-nums flex items-center gap-0.5 ${color}`}>
            <IndianRupee className="w-4 h-4 opacity-80" />
            {(amount ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{count ?? 0} referral(s)</div>
        </>
      )}
    </div>
  );
}

function BigCard({ title, count, amount, testId, color, icon, caption }) {
  const colorMap = {
    teal: 'from-teal-950/50 border-teal-800/60 text-teal-100',
    emerald: 'from-emerald-950/60 border-emerald-800/60 text-emerald-100',
    amber: 'from-amber-950/60 border-amber-800/60 text-amber-100',
  };
  return (
    <div data-testid={testId} className={`bg-gradient-to-br ${colorMap[color]} to-slate-900 border rounded-2xl p-5`}>
      <div className="flex items-center gap-2 text-xs opacity-80 uppercase tracking-widest mb-2">{icon}{title}</div>
      <div className="flex items-baseline gap-2">
        <span data-testid={`${testId}-amount`} className="text-3xl font-bold tabular-nums flex items-center gap-1"><IndianRupee className="w-5 h-5 opacity-80" />{formatINR(amount)}</span>
      </div>
      <div className="text-[11px] opacity-70 mt-1"><span data-testid={`${testId}-count`}>{count}</span> referral{count === 1 ? '' : 's'}{caption ? ` · ${caption}` : ''}</div>
    </div>
  );
}

function DoctorStatementModal({ data, loading, dateFrom, dateTo, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="doctor-statement-modal" className="bg-slate-900 border border-slate-800 w-full max-w-3xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 p-5 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-teal-400">Referral Doctor Statement</div>
            <div className="text-xl font-bold text-white mt-0.5">
              {loading ? <span className="text-slate-500">Loading…</span> : (data?.doctor?.name || '—')}
            </div>
            {data?.doctor && (
              <div className="text-[11px] text-slate-400 mt-1 flex flex-wrap items-center gap-3">
                {data.doctor.village && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{data.doctor.village}</span>}
                {data.doctor.phone_number && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{data.doctor.phone_number}</span>}
                {(dateFrom || dateTo) && <span>Period: {dateFrom || '…'} → {dateTo || '…'}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="close-statement-btn" className="text-slate-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-400" /></div>
        ) : data ? (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatCell label="Total Referrals" value={data.summary.total_referrals} />
              <StatCell label="Total Amount" value={`₹${formatINR(data.summary.total_amount)}`} />
              <StatCell label="Total Paid" value={`₹${formatINR(data.summary.paid_amount)}`} accent="emerald" />
              <StatCell label="Total Pending" value={`₹${formatINR(data.summary.pending_amount)}`} accent="amber" />
            </div>

            <div className="overflow-x-auto bg-slate-950/60 border border-slate-800 rounded-xl">
              <table className="w-full text-left text-sm" data-testid="statement-transactions-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-800">
                    <th className="py-2 px-3 font-semibold">Date</th>
                    <th className="py-2 px-3 font-semibold">Referral ID</th>
                    <th className="py-2 px-3 font-semibold">Patient</th>
                    <th className="py-2 px-3 font-semibold text-right">Amount</th>
                    <th className="py-2 px-3 font-semibold">Status</th>
                    <th className="py-2 px-3 font-semibold">Payment Date</th>
                    <th className="py-2 px-3 font-semibold">Voucher</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {data.transactions.length === 0 ? (
                    <tr><td colSpan={7} className="py-6 text-center text-slate-500 text-xs">No transactions in this period.</td></tr>
                  ) : (
                    data.transactions.map((t) => (
                      <tr key={t.id} data-testid={`statement-txn-${t.id}`} className="hover:bg-slate-800/30">
                        <td className="py-2 px-3 text-xs text-slate-400">{formatDate(t.created_at)}</td>
                        <td className="py-2 px-3 font-mono text-xs text-teal-300">{t.referral_id}</td>
                        <td className="py-2 px-3 text-xs text-slate-200">{t.patient_name}</td>
                        <td className="py-2 px-3 text-right text-xs text-white tabular-nums">₹{formatINR(t.final_referral_amount)}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${t.payment_status === 'paid' ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800' : 'bg-amber-950/80 text-amber-300 border border-amber-800'}`}>
                            {t.payment_status}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-xs text-slate-400">{t.payment_date ? formatDate(t.payment_date) : '—'}</td>
                        <td className="py-2 px-3 text-xs font-mono text-slate-300">{t.voucher_number || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatCell({ label, value, accent }) {
  const color = accent === 'emerald' ? 'text-emerald-300' : accent === 'amber' ? 'text-amber-300' : 'text-white';
  return (
    <div className="bg-slate-950/60 border border-slate-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-base font-semibold mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
